/**
 * Lecture des paramètres du lien pré-rempli `/new` (spec §3.4).
 *
 *   /new?title=Repas%20du%20club&dates=2026-10-10,2026-10-17&slots=19:00-22:00&src=claude
 *
 * - `title` (alias `titre`), `dates` (alias `date`, `DD/MM/YYYY` converties),
 *   `slots` (pour toutes les dates), `times` (`YYYY-MM-DD@HH:MM-HH:MM`,
 *   prioritaire sur `slots`), `description`, `lang` (fr|en|es), `src`.
 * - Toujours en mode SOUPLE : on garde ce qui est valide, on signale le reste.
 * - Rien n'est créé ici : fonction pure, rejouable à chaque rechargement.
 */
import { frenchDateToIso } from "./dates.js";
import { aiPollIssue, type AiPollIssue } from "./errors.js";
import { isAiPollLanguage, type AiPollLanguage } from "./messages.js";
import {
  validatePollInput,
  type PollInput,
  type ValidationResult,
} from "./validate.js";

export const AI_SOURCES = [
  "claude",
  "chatgpt",
  "lechat",
  "gemini",
  "perplexity",
  "other",
] as const;
export type AiSource = (typeof AI_SOURCES)[number];

export interface ParsedNewPollParams {
  /** Entrée reconstituée, avant validation. */
  input: PollInput;
  /** `null` si `src` absent ; valeur inconnue → `other`. */
  source: AiSource | null;
  /** `null` si `lang` absent ou non pris en charge (garder la détection i18n). */
  language: AiPollLanguage | null;
  /** Résultat de `validatePollInput` en mode souple. */
  validation: ValidationResult;
  /** Avertissements de lecture + de validation (codes stables + clés i18n). */
  warnings: AiPollIssue[];
}

export interface ParseNewPollContext {
  now?: Date;
  tz?: string;
}

function splitList(values: string[]): string[] {
  return values
    .flatMap((v) => v.split(/[,;\n]+/))
    .map((v) => v.trim())
    .filter(Boolean);
}

/** ISO tel quel, `DD/MM/YYYY` converti ; sinon la valeur brute (signalée à la validation). */
function normalizeDate(value: string): string {
  return frenchDateToIso(value) ?? value;
}

export function normalizeSource(
  raw: string | null | undefined,
): AiSource | null {
  if (raw === null || raw === undefined || raw.trim() === "") return null;
  const key = raw.toLowerCase().replace(/[^a-z]/g, "");
  return (AI_SOURCES as readonly string[]).includes(key)
    ? (key as AiSource)
    : "other";
}

export function parseNewPollParams(
  params: URLSearchParams | string,
  ctx: ParseNewPollContext = {},
): ParsedNewPollParams {
  const search =
    typeof params === "string"
      ? new URLSearchParams(
          params.includes("?") ? params.slice(params.indexOf("?") + 1) : params,
        )
      : params;

  const all = (...names: string[]) => names.flatMap((n) => search.getAll(n));
  const first = (...names: string[]) => {
    for (const n of names) {
      const v = search.get(n);
      if (v !== null) return v;
    }
    return null;
  };

  const title = first("title", "titre") ?? "";
  const description = first("description");
  const rawLang = first("lang");
  const dates = splitList(all("dates", "date")).map(normalizeDate);
  const timeSlots = splitList(all("slots"));

  const timesByDate: Record<string, string[]> = {};
  const unreadableTimes: string[] = [];
  for (const entry of splitList(all("times"))) {
    const at = entry.indexOf("@");
    if (at <= 0) {
      unreadableTimes.push(entry);
      continue;
    }
    const date = normalizeDate(entry.slice(0, at).trim());
    const slot = entry.slice(at + 1).trim();
    (timesByDate[date] ??= []).push(slot);
  }

  const language = isAiPollLanguage(rawLang) ? rawLang : null;

  const input: PollInput = {
    title,
    dates,
    timeSlots,
    timesByDate: Object.keys(timesByDate).length > 0 ? timesByDate : undefined,
    description,
    language: rawLang,
  };

  const validation = validatePollInput(input, {
    now: ctx.now,
    tz: ctx.tz,
    mode: "lenient",
  });

  const warnings: AiPollIssue[] = [];
  if (unreadableTimes.length > 0) {
    warnings.push(
      aiPollIssue("INVALID_FORMAT", "warnings.invalid_slots_dropped", {
        params: { count: unreadableTimes.length },
        details: { field: "times", values: unreadableTimes },
        language: language ?? undefined,
      }),
    );
  }
  warnings.push(...validation.warnings);

  return {
    input,
    source: normalizeSource(first("src")),
    language,
    validation,
    warnings,
  };
}
